<template>
  <form @submit.prevent="verifyOTP">
    <v-card>
      <v-card-title>{{ t("form.login.sms_verify") }}</v-card-title>
      <v-card-text>
        <div class="popup__desc">
          {{ createSubText }}
        </div>
        <div class="app__form">
          <div class="formItem formItem--1x12 formItem--link">
            <v-text-field
              id="otp"
              v-model="otp"
              :label="t('form.login.sms_code')"
              :rules="[rules.required, rules.isPositiveNumber]"
              autofocus
              :disabled="loading"
              :error-messages="get(errorMessages, 'otp')"
              @change="resetErrorMessages('otp')"
            >
              <template #message="{ message }">
                <span v-html="message" />
              </template>
            </v-text-field>
            <div class="d-sm-flex align-sm-center justify-space-between ga-2 mt-4">
              <v-checkbox
                v-if="enableRememberMe"
                v-model="rememberMe"
                class="flex-shrink-0"
              >
                <template #label>
                  <span class="color--grayTone8">{{ `${t('form.login.remember_for')} ${rememberMeTokenValidity} ${t('form.login.days')}` }}</span>
                  <v-tooltip
                    v-model="showTooltip"
                    location="top"
                    open-delay="300"
                    class="tooltip--info"
                  >
                    <template #activator="{ props }">
                      <v-icon
                        icon="up:circle-info"
                        size="x-small"
                        class="cursor-help ml-1"
                        v-bind="props"
                        @click="showTooltip = !showTooltip"
                      />
                    </template>
                    {{ t('form.login.remember_me_tooltip_text') }}
                  </v-tooltip>
                </template>
              </v-checkbox>
              <v-btn
                id="otp_again"
                variant="text"
                class="link link--relative text-right cursor-pointer d-inline-flex mt-sm-0 mt-2"
                :disabled="loading || resendBtnLoading || timer > 0"
                @click.prevent="resendVerifyOTP"
              >
                {{ buttonText }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          id="submit_btn"
          class="mbtn mbtn--green"
          size="x-large"
          type="submit"
          :loading="loading"
        >
          <slot name="btn-text">
            {{ t("form.login.finish") }}
          </slot>
        </v-btn>
      </v-card-actions>
    </v-card>
  </form>
</template>

<script setup lang="ts">

import {get} from "lodash-es";

const {rules} = useFormValidators();
const authStore = useAuthStore();
const {t} = useI18n();
const { $api } = useNuxtApp() as { $api: IApiInstance };
const config = useRuntimeConfig();
const appConfig = useAppConfig();
const {notify} = useNotification();
const localePath = useLocalePath();
const _accessTokenCookie = useCookie<string | null>(`auth:${appConfig.appName}:${config.public.region}:access-token`);
const _refreshTokenCookie = useCookie<string | null>(`auth:${appConfig.appName}:${config.public.region}:refresh-token`);
const {timer, startCountdown} = useCountdown();
const rememberMe = ref<boolean>(false);
const showTooltip = ref<boolean>(false);

const props = withDefaults(
  defineProps<{
    phone: string | null,
    email: string | null,
    token: string | null,
    uid?: string | null,
    verifyOnly?: boolean,
    isFromBuyout?: boolean,
    shouldVerifyToken?: boolean,
    enableRememberMe?: boolean,
    rememberMeTokenValidity?: number
  }>(),
  {
    uid: null,
    verifyOnly: false,
    isFromBuyout: false,
    shouldVerifyToken: true,
    enableRememberMe: false,
    rememberMeTokenValidity: 30
  }
);

const emit = defineEmits(["verifyOtp", "closeDialog"]);

const otp = ref<string>("");
const errorMessages = ref<Record<string, string[]> | null>(null);
const loading = ref<boolean>(false);
const resendBtnLoading = ref<boolean>(false);

const buttonText = computed<string>(() => {
  if (timer.value > 0) {
    return `${t("form.login.resend_otp")} (${timer.value}s)`;
  } else {
    return t("form.login.resend_otp");
  }
});

startCountdown();

const createSubText = computed<string>(() => {
  return t("form.login.sms_verify_sub", {phone: useDisplayPhone(props.phone)});
});

async function resendVerifyOTP() {
  resendBtnLoading.value = true;
  const { error } = await $api.auth.userOtp({phone: props.phone});
  if (!error.value) {
    notify({
      title: t("form.common.otp_resend"),
      type: "success"
    });
  } else {
    notify({
      title: t("form.common.error"),
      text: error.value?.data?.detail ?? t("general.error_sending_request"),
      type: "error"
    });
  }
  startCountdown();
  resendBtnLoading.value = false;
}

async function sendOTP() {
  return await resendVerifyOTP();
}

async function verifyOTP() {
  loading.value = true;
  const rememberMeToken = rememberMe.value ? crypto.randomUUID() : null;
  const {data, error} = await $api.auth.verifyOtp({
    login_token: props.token,
    otp: otp.value,
    uid: props.uid,
    should_verify_token: props.shouldVerifyToken,
    remember_me_token: rememberMeToken,
    otp_verify_only: props.verifyOnly
  });

  if (error.value) {
    if (error.value?.data?.otp) {
      errorMessages.value = error.value?.data;
    } else if (error.value?.data?.registration_token_expired) {
      errorMessages.value = error.value?.data;
      errorMessages.value!.otp = [getTranslatedRegistrationExpiredErrorMessage()]; // to tell user that his registration token expired
    } else {
      notify({
        title: t("form.common.error"),
        text: error.value!.data?.detail ?? t("general.error_sending_request"),
        type: "error"
      });
    }
    loading.value = false;
  }
  if (data.value) {
    if (!props.verifyOnly) {
      authStore.accessToken = data.value!.access_token;
      authStore.refreshToken = data.value!.refresh_token;
      _refreshTokenCookie.value = data.value!.refresh_token;
      _accessTokenCookie.value = data.value!.access_token;
      await nextTick(authStore.getSession);
      if (props.isFromBuyout) {
        emit("closeDialog");
      }
    }
    if (rememberMe.value) {
      authStore.rememberMeTokenCookie = rememberMeToken;
    }
    await emit("verifyOtp", !!authStore.accessToken && authStore.isLoggedIn, () => loading.value = false);
  }
}

function resetErrorMessages(key: string) {
  if (errorMessages?.value) {
    delete errorMessages.value[key];
  }
}

function getTranslatedRegistrationExpiredErrorMessage() {
  const message = t("form.register.registration_token_expired_start");
  const linkText = t("form.register.here");
  const link = `<a href="${localePath({name: "forgotten-password"})}">${linkText}</a>`;

  return `${message} ${link})`;
}

defineExpose({sendOTP});
</script>
